import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { isIOSApp } from '../utils/platformDetection';
import { billingAttribution } from '../services/billingAttribution';
import '../styles/components.css';

import { BrandPendulumMark } from './BrandPendulumMark';
import { Dropdown } from './Dropdown';

interface FooterProps {
  currency?: string;
  setCurrency?: (val: string) => void;
}

type IconProps = React.SVGProps<SVGSVGElement>;

const LinkedinIcon: React.FC<IconProps> = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path fill="currentColor" d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V8.98h3.42v1.57h.05a3.75 3.75 0 0 1 3.37-1.85c3.61 0 4.27 2.37 4.27 5.46v6.29ZM5.32 7.41a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12Zm1.78 13.04H3.54V8.98H7.1v11.47Z" />
  </svg>
);

const InstagramIcon: React.FC<IconProps> = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <rect x="3" y="3" width="18" height="18" rx="5" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="12" cy="12" r="4.25" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="17.4" cy="6.7" r="1.15" fill="currentColor" />
  </svg>
);

const YoutubeIcon: React.FC<IconProps> = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path fill="currentColor" d="M23.5 6.19a3 3 0 0 0-2.12-2.13C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.51A3 3 0 0 0 .5 6.19 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.81 3 3 0 0 0 2.12 2.13c1.88.51 9.38.51 9.38.51s7.5 0 9.38-.51a3 3 0 0 0 2.12-2.13A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.81ZM9.6 15.64V8.36L15.86 12 9.6 15.64Z" />
  </svg>
);

const XIcon: React.FC<IconProps> = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path fill="currentColor" d="M18.9 2h3.68l-8.04 9.19L24 22h-7.41l-5.8-7.59L4.15 22H.47l8.59-9.82L0 2h7.59l5.24 6.93L18.9 2Zm-1.29 18.1h2.04L6.48 3.8H4.29L17.61 20.1Z" />
  </svg>
);

const AppleIcon: React.FC<IconProps> = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path fill="currentColor" d="M18.71 12.24c.02-2.08 1.7-3.08 1.78-3.13a3.82 3.82 0 0 0-3.01-1.63c-1.27-.13-2.5.76-3.15.76-.66 0-1.65-.75-2.73-.73a4.48 4.48 0 0 0-3.76 2.3c-1.63 2.82-.41 6.97 1.15 9.25.78 1.12 1.69 2.37 2.77 2.33 1.06-.04 1.46-.75 2.75-.75 1.27 0 1.65.75 2.76.72 1.13-.01 1.85-1.12 2.6-2.25a9.2 9.2 0 0 0 1.19-2.43 4.3 4.3 0 0 1-2.35-4.44ZM16.65 6.14a4.36 4.36 0 0 0 1-3.14 4.44 4.44 0 0 0-2.89 1.49 4.15 4.15 0 0 0-1.03 3.02 3.67 3.67 0 0 0 2.92-1.37Z" />
  </svg>
);

const PlayStoreIcon: React.FC<IconProps> = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
    <path fill="#00d7fe" d="M3.6 2.36a1.8 1.8 0 0 0-.39 1.15v16.98c0 .44.14.83.38 1.15l9.33-9.65L3.6 2.36Z" />
    <path fill="#00f076" d="m16.04 8.77-10.5-6.08a1.86 1.86 0 0 0-1.3-.27L13.72 12l2.32-3.23Z" />
    <path fill="#ffcf00" d="m4.24 21.58 11.82-6.82-2.34-2.77-9.48 9.59Z" />
    <path fill="#ff3a44" d="m20.01 11.07-3.97-2.3L13.72 12l2.34 2.77 3.95-2.28c.8-.46.8-.96 0-1.42Z" />
  </svg>
);

const socialLinks = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/8080ai/', Icon: LinkedinIcon },
  { label: 'Instagram', href: 'https://www.instagram.com/8080.ai_/', Icon: InstagramIcon },
  { label: 'YouTube', href: 'https://www.youtube.com/@team8080ai', Icon: YoutubeIcon },
  { label: 'X', href: 'https://x.com/8080AI', Icon: XIcon },
];

export const Footer: React.FC<FooterProps> = ({ currency, setCurrency }) => {
  const showExplore = !isIOSApp();
  const location = useLocation();
  const isExplorePage = location.pathname === '/explore';

  return (
    <footer id="contact" className={`landing-footer ${isExplorePage ? 'footer--sticky' : ''}`}>
      <div className="footer-shell">
        <div className="footer-main">
          <div className="footer-brand-column">
            <Link className="brand-text brand-button footer-brand" to="/" aria-label="8080 AI home">
              <BrandPendulumMark className="brand-mark" />
              <span className="brand-button__label">8080.AI</span>
            </Link>
            <p className="footer-tagline">Your words can change the world.</p>
            <nav className="footer-socials" aria-label="8080 AI social media">
              {socialLinks.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  className="footer-social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Follow 8080 AI on ${label}`}
                  title={label}
                >
                  <Icon />
                </a>
              ))}
            </nav>
          </div>

          <nav className="footer-links-column" aria-labelledby="footer-product-heading">
            <h2 id="footer-product-heading" className="footer-heading">Product</h2>
            {showExplore && <a href="/explore" className="footer-link">Explore</a>}
            <a href="/pricing" className="footer-link"
              onClick={() => billingAttribution.markNavigation('site-footer:pricing')}>Pricing</a>
            <a href="/about" className="footer-link">About</a>
          </nav>

          <nav className="footer-links-column" aria-labelledby="footer-legal-heading">
            <h2 id="footer-legal-heading" className="footer-heading">Legal</h2>
            <a href="/privacy" className="footer-link">Privacy Policy</a>
            <a href="/terms" className="footer-link">Terms and Conditions</a>
          </nav>

          <div className="footer-apps-column">
            <h2 className="footer-heading">Get the app</h2>
            <div className="footer-store-links">
              <a
                href="https://apps.apple.com/us/app/8080-ai/id6761405121"
                className="footer-store-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download 8080 AI on the App Store"
              >
                <AppleIcon className="footer-store-icon" />
                <span><small>Download on the</small>App Store</span>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.app.ai8080&amp;pcampaignid=web_share"
                className="footer-store-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get 8080 AI on Google Play"
              >
                <PlayStoreIcon className="footer-store-icon" />
                <span><small>GET IT ON</small>Google Play</span>
              </a>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} 8080 AI. All rights reserved.</p>
          {currency && setCurrency && (
            <div className="footer-currency-selector">
              <label htmlFor="footer-currency">Currency</label>
              <Dropdown
                id="footer-currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="currency-dropdown-footer"
              >
                <option value="USD">USD ($)</option>
                <option value="INR">INR (₹)</option>
              </Dropdown>
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};
